
import { Building, Shield, Utensils, Globe, Stethoscope, Scissors, Phone, PhoneCall, Bot, Zap, HeadphonesIcon, Calendar, FileText, ShoppingCart, Users, MessageSquare } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
import { Link } from "react-router-dom";

interface UseCasesProps {
  homepageData?: {
    useCasesNumber?: string;
    useCasesHeadline?: string;
    useCasesDescription?: string;
  };
}

export const UseCases = ({ homepageData }: UseCasesProps) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("inbound");

  const tabOptions = [
    { value: "inbound", label: "Inbound Voice Agents" },
    { value: "outbound", label: "Outbound Voice Agents" },
    { value: "copilots", label: "AI Copilots" },
    { value: "automation", label: "Automation" }
  ];

  const inboundUseCases = [
    {
      icon: Building,
      title: "Realtor office",
      description: "Property inquiries, viewings, lead qualification",
      url: "/services/inbound-voice-agents/realtor-office"
    },
    {
      icon: Shield,
      title: "Insurance companies",
      description: "Claims processing, policy help, customer support",
      url: "/services/inbound-voice-agents/insurance-companies"
    },
    {
      icon: Stethoscope,
      title: "Healthcare providers",
      description: "Appointment scheduling, patient inquiries, triage",
      url: "/services/inbound-voice-agents/healthcare-providers"
    },
    {
      icon: Utensils,
      title: "Restaurant chains",
      description: "Reservations, menu inquiries, order taking",
      url: "/services/inbound-voice-agents/restaurant-chains"
    },
    {
      icon: HeadphonesIcon,
      title: "Customer service",
      description: "24/7 support, FAQ responses, ticket routing",
      url: "/services/inbound-voice-agents/customer-service"
    },
    {
      icon: Globe,
      title: "SaaS companies",
      description: "Technical support, billing inquiries, onboarding",
      url: "/services/inbound-voice-agents/saas-companies"
    }
  ];

  const outboundUseCases = [
    {
      icon: Phone,
      title: "Sales outreach",
      description: "Lead qualification, appointment setting, follow-ups",
      url: "/services/outbound-voice-agents/sales-outreach"
    },
    {
      icon: Users,
      title: "Customer retention",
      description: "Renewal reminders, satisfaction surveys, upselling",
      url: "/services/outbound-voice-agents/customer-retention"
    },
    {
      icon: Calendar,
      title: "Appointment reminders",
      description: "Medical appointments, service bookings, confirmations",
      url: "/services/outbound-voice-agents/appointment-reminders"
    },
    {
      icon: MessageSquare,
      title: "Survey collection",
      description: "Customer feedback, market research, NPS surveys",
      url: "/services/outbound-voice-agents/market-research"
    },
    {
      icon: FileText,
      title: "Debt collection",
      description: "Payment reminders, negotiation, account updates",
      url: "/services/outbound-voice-agents/debt-collection"
    },
    {
      icon: ShoppingCart,
      title: "Marketing agency",
      description: "Product launches, promotional offers, event invitations",
      url: "/services/outbound-voice-agents/marketing-agency"
    }
  ];

  const copilotUseCases = [
    {
      icon: HeadphonesIcon,
      title: "Agent assistance",
      description: "Real-time guidance, script suggestions, objection handling",
      url: "/services/ai-copilots/agent-assistance"
    },
    {
      icon: FileText,
      title: "Document processing",
      description: "Contract review, form completion, data extraction",
      url: "/services/ai-copilots/document-processing"
    },
    {
      icon: Users,
      title: "Meeting transcription",
      description: "Real-time notes, action items, summary generation",
      url: "/services/ai-copilots/meeting-transcription"
    },
    {
      icon: Bot,
      title: "Email drafting",
      description: "Automated responses, tone adjustment, personalization",
      url: "/services/ai-copilots/email-drafting"
    },
    {
      icon: Calendar,
      title: "Scheduling optimization",
      description: "Calendar management, meeting coordination, availability",
      url: "/services/ai-copilots/scheduling-optimization"
    },
    {
      icon: Globe,
      title: "Research assistance",
      description: "Market analysis, competitor research, lead enrichment",
      url: "/services/ai-copilots/research-assistance"
    }
  ];

  const automationUseCases = [
    {
      icon: FileText,
      title: "Document workflow",
      description: "Invoice processing, contract generation, approval routing",
      url: "/services/automation/document-workflow"
    },
    {
      icon: Users,
      title: "Employee onboarding",
      description: "Account setup, training schedules, resource allocation",
      url: "/services/automation/employee-onboarding"
    },
    {
      icon: ShoppingCart,
      title: "Inventory management",
      description: "Stock monitoring, reorder alerts, supplier coordination",
      url: "/services/automation/inventory-management"
    },
    {
      icon: MessageSquare,
      title: "Customer communications",
      description: "Welcome sequences, status updates, notification delivery",
      url: "/services/automation/customer-communications"
    },
    {
      icon: Calendar,
      title: "Process orchestration",
      description: "Workflow automation, task delegation, progress tracking",
      url: "/services/automation/process-orchestration"
    },
    {
      icon: Zap,
      title: "Data integration",
      description: "System synchronization, report generation, analytics",
      url: "/services/automation/data-integration"
    }
  ];

  const renderUseCaseGrid = (useCases: Array<{icon: any, title: string, description: string, url?: string}>) => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
      {useCases.map((useCase, index) => {
        const IconComponent = useCase.icon;
        const CardContent = (
          <div className="bg-white rounded-lg p-8 hover:shadow-lg transition-shadow">
            <div className="mb-6">
              <IconComponent className="h-8 w-8 text-violet-500" />
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-3">
              {useCase.title}
            </h3>
            <p className="text-gray-600">
              {useCase.description}
            </p>
          </div>
        );

        // If the use case has a URL, wrap it in a Link component
        if ('url' in useCase && useCase.url) {
          return (
            <Link key={index} to={useCase.url} className="block hover-scale">
              {CardContent}
            </Link>
          );
        }

        // Otherwise, return the card without a link
        return <div key={index}>{CardContent}</div>;
      })}
    </div>
  );

  const getTabContent = (tabValue: string) => {
    switch (tabValue) {
      case "inbound":
        return {
          title: "Inbound Voice Agents",
          description: "Handle incoming calls with intelligent voice agents that can answer questions, schedule appointments, and provide customer support 24/7.",
          useCases: inboundUseCases
        };
      case "outbound":
        return {
          title: "Outbound Voice Agents",
          description: "Proactively reach out to customers with personalized calls for sales, surveys, reminders, and follow-ups at scale.",
          useCases: outboundUseCases
        };
      case "copilots":
        return {
          title: "AI Copilots",
          description: "Enhance human productivity with AI assistants that provide real-time support, suggestions, and automation for complex tasks.",
          useCases: copilotUseCases
        };
      case "automation":
        return {
          title: "Automation",
          description: "Streamline business processes with intelligent automation that handles repetitive tasks and complex workflows without human intervention.",
          useCases: automationUseCases
        };
      default:
        return {
          title: "Inbound Voice Agents",
          description: "Handle incoming calls with intelligent voice agents that can answer questions, schedule appointments, and provide customer support 24/7.",
          useCases: inboundUseCases
        };
    }
  };

  const currentContent = getTabContent(activeTab);

  return (
    <section id="use-cases" className="py-20 px-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-sm text-purple-500 font-medium mb-4">{homepageData?.useCasesNumber || "002"}</div>
        <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-8 max-w-4xl">
          {homepageData?.useCasesHeadline || "Custom solutions for every use case"}
        </h2>
        <p className="text-xl text-gray-600 mb-16 max-w-4xl">
          {homepageData?.useCasesDescription || "Customer support, front desk, outbound sales, lead generation, tele health, food ordering, transportation logistics, employee training, roleplay, or whatever else you want to build."}
        </p>
        
        {/* Mobile Dropdown */}
        {isMobile ? (
          <div className="mb-8">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full bg-white border-gray-300">
                <SelectValue placeholder="Select use case type" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                {tabOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="hover:bg-gray-100">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="mt-8">
              <h3 className="text-2xl font-medium text-gray-900 mb-4">{currentContent.title}</h3>
              <p className="text-gray-600 mb-6">{currentContent.description}</p>
              {renderUseCaseGrid(currentContent.useCases)}
            </div>
          </div>
        ) : (
          /* Desktop Tabs */
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 bg-gray-200 p-1 rounded-lg h-auto">
              <TabsTrigger 
                value="inbound" 
                className="px-6 py-3 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"
              >
                Inbound Voice Agents
              </TabsTrigger>
              <TabsTrigger 
                value="outbound"
                className="px-6 py-3 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"
              >
                Outbound Voice Agents
              </TabsTrigger>
              <TabsTrigger 
                value="copilots"
                className="px-6 py-3 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"
              >
                AI Copilots
              </TabsTrigger>
              <TabsTrigger 
                value="automation"
                className="px-6 py-3 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"
              >
                Automation
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="inbound">
              <div className="mt-8">
                <h3 className="text-2xl font-medium text-gray-900 mb-4">Inbound Voice Agents</h3>
                <p className="text-gray-600 mb-6">
                  Handle incoming calls with intelligent voice agents that can answer questions, 
                  schedule appointments, and provide customer support 24/7.
                </p>
                {renderUseCaseGrid(inboundUseCases)}
              </div>
            </TabsContent>
            
            <TabsContent value="outbound">
              <div className="mt-8">
                <h3 className="text-2xl font-medium text-gray-900 mb-4">Outbound Voice Agents</h3>
                <p className="text-gray-600 mb-6">
                  Proactively reach out to customers with personalized calls for sales, 
                  surveys, reminders, and follow-ups at scale.
                </p>
                {renderUseCaseGrid(outboundUseCases)}
              </div>
            </TabsContent>
            
            <TabsContent value="copilots">
              <div className="mt-8">
                <h3 className="text-2xl font-medium text-gray-900 mb-4">AI Copilots</h3>
                <p className="text-gray-600 mb-6">
                  Enhance human productivity with AI assistants that provide real-time support, 
                  suggestions, and automation for complex tasks.
                </p>
                {renderUseCaseGrid(copilotUseCases)}
              </div>
            </TabsContent>
            
            <TabsContent value="automation">
              <div className="mt-8">
                <h3 className="text-2xl font-medium text-gray-900 mb-4">Automation</h3>
                <p className="text-gray-600 mb-6">
                  Streamline business processes with intelligent automation that handles 
                  repetitive tasks and complex workflows without human intervention.
                </p>
                {renderUseCaseGrid(automationUseCases)}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </section>
  );
};
